// UGAIN — Make it happen (photographic action menu). Approved design, 2026-11-05.
// Self-contained: depends only on React and make-it-happen.css.
// Integration: render <MakeItHappen onAction={...} /> where the existing Create menu renders,
// and route each action to its EXISTING workflow (see INTEGRATION.md). No other page is affected.
import type { ReactNode } from 'react';
import './make-it-happen.css';

export type MakeItHappenAction = 'post' | 'propose' | 'share' | 'request';

export interface MakeItHappenProps {
  /** Called with the tapped action. Route to the existing post / propose / share / request flows. */
  onAction: (action: MakeItHappenAction) => void;
  /** Public URL prefix for assets (Vite: import.meta.env.BASE_URL). Default '/'. */
  assetBase?: string;
  /** Optional content below the grid (e.g. the existing "Update <activity>" shortcut card). */
  children?: ReactNode;
}

interface CardDef {
  action: MakeItHappenAction;
  glyph: string;
  label: string;
  desc: string;
  /** File stem under public/photos/ — 360 and 720 px square WebP. Replace files to change photos. */
  photo: string;
  tone: 'ink' | 'violet';
  dot: 'violet' | 'white' | 'soft' | 'ink';
}

export const MIH_CARDS: readonly CardDef[] = [
  { action: 'post', glyph: '✎', label: 'Post update', desc: 'Progress, photos, wins', photo: 'mih-capture', tone: 'ink', dot: 'violet' },
  { action: 'propose', glyph: '+', label: 'Propose activity', desc: 'Any type, any UNIT', photo: 'mih-gathering', tone: 'violet', dot: 'white' },
  { action: 'share', glyph: '↗', label: 'Share content', desc: 'Approved campaigns', photo: 'mih-camera', tone: 'ink', dot: 'soft' },
  { action: 'request', glyph: '?', label: 'Request help', desc: 'Photographers, volunteers…', photo: 'mih-hands', tone: 'violet', dot: 'ink' },
];

/** Subtle lavender network motif behind the heading (decorative). */
function NetworkMotif() {
  const nodes: [number, number, number][] = [[214, 34, 3], [262, 22, 2.2], [300, 52, 2.6], [338, 30, 2], [252, 76, 2], [318, 92, 2.8], [370, 64, 2.2], [224, 110, 1.8], [356, 118, 2], [286, 132, 2.2]];
  const links: [number, number][] = [[0, 1], [1, 3], [0, 4], [1, 2], [2, 5], [3, 6], [4, 7], [5, 6], [5, 8], [4, 9], [9, 5], [2, 4]];
  return (
    <svg className="mih-motif" viewBox="160 0 240 160" aria-hidden="true" focusable="false" preserveAspectRatio="xMaxYMin meet">
      <defs>
        <radialGradient id="mihGlow" cx="72%" cy="40%" r="60%"><stop offset="0" stopColor="#9D85FF" stopOpacity=".34" /><stop offset="1" stopColor="#9D85FF" stopOpacity="0" /></radialGradient>
      </defs>
      <rect x="160" y="0" width="240" height="160" fill="url(#mihGlow)" />
      <g fill="none" stroke="#9D85FF" strokeWidth=".8" strokeOpacity=".55">
        <ellipse cx="300" cy="84" rx="92" ry="54" strokeOpacity=".28" />
        <ellipse cx="300" cy="84" rx="46" ry="54" strokeOpacity=".22" />
        <path d="M208 84h184" strokeOpacity=".22" />
        {links.map(([i, j]) => <path key={`${i}-${j}`} d={`M${nodes[i][0]} ${nodes[i][1]} Q ${(nodes[i][0] + nodes[j][0]) / 2} ${Math.min(nodes[i][1], nodes[j][1]) - 14} ${nodes[j][0]} ${nodes[j][1]}`} strokeDasharray="2 3" />)}
      </g>
      <g fill="#8B6DFF">{nodes.map(([x, y, r], i) => <circle key={i} cx={x} cy={y} r={r} className={i % 3 === 0 ? 'mih-node' : undefined} />)}</g>
    </svg>
  );
}

export function MakeItHappen({ onAction, assetBase = '/', children }: MakeItHappenProps) {
  const base = assetBase.endsWith('/') ? assetBase : `${assetBase}/`;
  return (
    <div className="mih">
      <div className="mih-head">
        <NetworkMotif />
        {/* Official UNIT mark from the existing asset; unmodified, 22 px as on Home. */}
        <img className="mih-mark" src={`${base}brand/unit-mark-violet.svg`} alt="UNIT" width={18} height={22} />
        <h1 className="mih-title">Make it<br />happen.</h1>
        <span className="mih-stroke" aria-hidden="true" />
        <p className="mih-sub">Post, propose, share or ask. Everything lands on the UGAIN Pulse.</p>
      </div>
      <div className="mih-grid">
        {MIH_CARDS.map((c) => (
          <button key={c.action} type="button" className={`mih-card mih-card--${c.tone}`} onClick={() => onAction(c.action)} aria-label={`${c.label}: ${c.desc}`}>
            <span className="mih-photo">
              <img src={`${base}photos/${c.photo}-360.webp`} srcSet={`${base}photos/${c.photo}-360.webp 360w, ${base}photos/${c.photo}-720.webp 720w`}
                sizes="(max-width: 480px) 50vw, 240px" alt="" loading="eager" decoding="async" width={360} height={360} />
            </span>
            <span className="mih-shade" aria-hidden="true" />
            <span aria-hidden="true" className={`mih-dot mih-dot--${c.dot}`}>{c.glyph}</span>
            <span className="mih-text">
              <span className="mih-label">{c.label}</span>
              <span className="mih-desc">{c.desc}</span>
            </span>
          </button>
        ))}
      </div>
      {children}
    </div>
  );
}
